"use strict";

/**
 * src/pages/analysis/sportsbookCatalog.shared.js
 *
 * PHASE 2D.1 — Shared provisional sportsbook catalog.
 *
 * This module is the SINGLE SOURCE OF TRUTH for the 48-sportsbook
 * catalog used by the "Sportsbook display settings" screen
 * (SportsbookSettings, in AnalysisStatsPage.jsx).
 *
 * IMPORTANT — PROVISIONAL IDs, NOT CANONICAL SHARP KEYS:
 *   Every `id` value in this catalog is a PROVISIONAL INTERNAL UI ID
 *   chosen for this phase only, to keep the current UI functional
 *   before real Sharp data-provider integration exists. It is NOT the
 *   official/canonical Sharp key for that sportsbook. Do NOT treat any
 *   `id` here as a canonical Sharp key, and do NOT invent or alias a
 *   raw Sharp key from it. Canonical key mapping will be introduced in
 *   a later phase once real Sharp data is available.
 *
 * Module contract:
 *   - Plain CommonJS (module.exports = {...}) so that:
 *       - the current CRA/Webpack + Babel frontend can consume this
 *         module via named imports, e.g.
 *         `import { SPORTSBOOK_GROUPS } from "./sportsbookCatalog.shared";`
 *       - a future backend module can `require()` it directly.
 *   - No browser globals (window / document / navigator / localStorage
 *     / etc.) are read or referenced.
 *   - No environment variables, filesystem access, network access, or
 *     database access.
 *   - No `console.*` calls anywhere in this file.
 *   - No side effects at require-time beyond constructing and
 *     deep-freezing the in-memory catalog data declared below.
 *   - Every group object, every item object, and every exported array
 *     is deep-frozen, so consumers cannot mutate the shared catalog.
 */

function deepFreeze(value) {
  if (value === null || typeof value !== "object") return value;
  if (Object.isFrozen(value)) return value;
  Object.getOwnPropertyNames(value).forEach((key) => {
    deepFreeze(value[key]);
  });
  return Object.freeze(value);
}

// Provisional catalog version tag for this phase. NOT a Sharp data
// version — purely an internal marker for this UI-only catalog.
const SPORTSBOOK_CATALOG_VERSION = "sharp-ui-2026-08-08-provisional";

// ── Golden catalog — 4 groups, 48 items total ──────────────────────────────
// Ordering within each group is intentional and must not be reordered.
const SPORTSBOOK_GROUPS = [
  {
    key: "major_us",
    title: "MAJOR US BOOKS",
    desc: "Nhóm nhà cái lớn tại thị trường Mỹ",
    items: [
      { id: "hard_rock_bet", label: "Hard Rock Bet", logoText: "HR", status: "active" },
      { id: "draftkings", label: "DraftKings", logoText: "DK", status: "active" },
      { id: "betrivers", label: "BetRivers", logoText: "BR", status: "active" },
      { id: "betmgm", label: "BetMGM", logoText: "MGM", status: "active" },
      { id: "thescore_bet", label: "theScore Bet", logoText: "TS", status: "active" },
      { id: "fanduel", label: "FanDuel", logoText: "FD", status: "active" },
      { id: "caesars", label: "Caesars", logoText: "CZ", status: "active" },
      { id: "fanatics", label: "Fanatics", logoText: "FN", status: "active" },
      { id: "fliff", label: "Fliff", logoText: "FL", status: "active" },
    ],
  },
  {
    key: "sharp",
    title: "SHARP BOOKS",
    desc: "Nhóm nhà cái sharp trong danh mục hiện tại",
    items: [
      { id: "pinnacle", label: "Pinnacle", logoText: "P", status: "active" },
      { id: "1xbet", label: "1xBet", logoText: "1X", status: "active" },
      { id: "circa", label: "Circa Sports", logoText: "CS", status: "active" },
      { id: "prophetx", label: "ProphetX", logoText: "PX", status: "active" },
      { id: "sbobet", label: "SBOBET", logoText: "SB", status: "active" },
      { id: "188bet", label: "188bet", logoText: "188", status: "inactive" },
    ],
  },
  {
    key: "regional_international",
    title: "REGIONAL & INTERNATIONAL",
    desc: "Nhóm nhà cái khu vực và quốc tế",
    items: [
      { id: "betonline", label: "BetOnline", logoText: "BO", status: "active" },
      { id: "galera_bet", label: "Galera.bet", logoText: "GB", status: "active" },
      { id: "ladbrokes", label: "Ladbrokes", logoText: "LB", status: "active" },
      { id: "saba", label: "SABA", logoText: "SA", status: "active" },
      { id: "bwin", label: "bwin", logoText: "bw", status: "active" },
      { id: "unibet_canada", label: "Unibet (Canada)", logoText: "UC", status: "active" },
      { id: "bovada", label: "Bovada", logoText: "BV", status: "active" },
      { id: "unibet_sweden", label: "Unibet (Sweden)", logoText: "US", status: "active" },
      { id: "unibet_romania", label: "Unibet (Romania)", logoText: "UR", status: "active" },
      { id: "ballybet", label: "Bally Bet", logoText: "BB", status: "active" },
      { id: "unibet_belgium", label: "Unibet (Belgium)", logoText: "UB", status: "active" },
      { id: "betparx", label: "betPARX", logoText: "BP", status: "active" },
      { id: "sportzino", label: "Sportzino", logoText: "SZ", status: "active" },
      { id: "betway", label: "Betway", logoText: "BW", status: "active" },
      { id: "stake", label: "Stake", logoText: "ST", status: "active" },
      { id: "18bet", label: "18Bet", logoText: "18", status: "active" },
      { id: "rebet", label: "Rebet", logoText: "RB", status: "active" },
      { id: "sportingbet_br", label: "Sportingbet BR", logoText: "SBR", status: "active" },
      { id: "betano", label: "Betano", logoText: "BT", status: "active" },
      { id: "goldrush", label: "Goldrush", logoText: "GR", status: "active" },
      { id: "underdog_fantasy", label: "Underdog Fantasy", logoText: "UF", status: "active" },
      { id: "unibet_denmark", label: "Unibet (Denmark)", logoText: "UD", status: "active" },
      { id: "bet365_us", label: "Bet365 US", logoText: "365", status: "active" },
      { id: "unibet", label: "Unibet", logoText: "U", status: "active" },
      { id: "skybet", label: "Sky Bet", logoText: "SKY", status: "active" },
      { id: "thrillzz", label: "Thrillzz", logoText: "TZ", status: "active" },
      { id: "betsson", label: "Betsson", logoText: "BS", status: "inactive" },
    ],
  },
  {
    key: "exchanges_prediction",
    title: "EXCHANGES & PREDICTION MARKETS",
    desc: "Sàn giao dịch và thị trường dự đoán",
    items: [
      { id: "kalshi", label: "Kalshi", logoText: "KA", status: "active" },
      { id: "polymarket", label: "Polymarket", logoText: "PM", status: "active" },
      { id: "sx_bet", label: "SX Bet", logoText: "SX", status: "active" },
      { id: "novig", label: "Novig", logoText: "NV", status: "active" },
      { id: "betfair", label: "Betfair", logoText: "BF", status: "active" },
      { id: "fanatics_markets", label: "Fanatics Markets", logoText: "FM", status: "active" },
    ],
  },
];

const SPORTSBOOK_OPTIONS = SPORTSBOOK_GROUPS.flatMap((group) => group.items);

const SELECTABLE_SPORTSBOOK_OPTIONS = SPORTSBOOK_OPTIONS.filter(
  (item) => item.status === "active"
);

const SELECTABLE_SPORTSBOOK_IDS = SELECTABLE_SPORTSBOOK_OPTIONS.map(
  (item) => item.id
);

function normalizeSportsbookId(value) {
  return String(value || "").trim().toLowerCase();
}

function getSportsbookById(id) {
  const normalized = normalizeSportsbookId(id);
  if (!normalized) return null;
  const found = SPORTSBOOK_OPTIONS.find((item) => item.id === normalized);
  return found || null;
}

function isSelectableSportsbookId(id) {
  const item = getSportsbookById(id);
  return !!item && item.status === "active";
}

// Deep-freeze the group tree (this also freezes every nested item
// object, since they are shared by reference with SPORTSBOOK_OPTIONS
// and SELECTABLE_SPORTSBOOK_OPTIONS below), then freeze each exported
// array wrapper.
deepFreeze(SPORTSBOOK_GROUPS);
deepFreeze(SPORTSBOOK_OPTIONS);
deepFreeze(SELECTABLE_SPORTSBOOK_OPTIONS);
deepFreeze(SELECTABLE_SPORTSBOOK_IDS);

module.exports = {
  SPORTSBOOK_CATALOG_VERSION,
  SPORTSBOOK_GROUPS,
  SPORTSBOOK_OPTIONS,
  SELECTABLE_SPORTSBOOK_OPTIONS,
  SELECTABLE_SPORTSBOOK_IDS,
  normalizeSportsbookId,
  getSportsbookById,
  isSelectableSportsbookId,
};
